import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Save, Loader2, RotateCcw, ChevronDown, ChevronRight, RefreshCw, Tag, User,
  Rss, Trash2, Plus, Radio, Search as SearchIcon, CheckCircle2, XCircle,
  Globe,
} from "lucide-react";
import { api } from "@/lib/api";
import { SETTINGS } from "@/constants/testIds";
import {
  COMMON_TIMEZONES,
  formatInTz,
  tzAbbreviation,
} from "@/lib/timezone";
import TwoFactorSettings from "@/components/TwoFactorSettings";
import InternalLinkIndexSettings from "@/components/InternalLinkIndexSettings";
import TelegramSection from "@/components/TelegramSection";
import StoryCardsSection from "@/components/StoryCardsSection";
import SocialConnectionsSection from "@/components/SocialConnectionsSection";
import WpcomConnectionSection from "@/components/WpcomConnectionSection";
import SourceNoteTemplateField from "@/components/SourceNoteTemplateField";
import NewsletterThemeEditor from "@/components/NewsletterThemeEditor";
import ImageLegendStyleEditor from "@/components/ImageLegendStyleEditor";
import MailerLiteAudience from "@/components/MailerLiteAudience";
import PromptBackupSection from "@/components/PromptBackupSection";
import BackupsSection from "@/components/BackupsSection";
import SalesInboxSection from "@/components/SalesInboxSection";

const HOUSE_RULE_LABELS = {
  identity: "Publication Identity",
  tone: "House Tone",
  community_news_standard: "Community News Standard",
  headlines: "Headline Rules",
  story_structure: "Story Structure",
  paragraph_rules: "Paragraph Rules",
  sentence_style: "Sentence Style",
  length_rules: "Article Length Rules",
  seo_structure: "SEO Structure",
  source_handling: "Source Handling",
  social_media_style: "Social Media Style",
  quick_rules: "Quick Rules",
};

const WRITER_FIELDS = [
  { key: "name", label: "Display name", rows: 1 },
  { key: "best_for", label: "Best for (beats)", rows: 3 },
  { key: "voice", label: "Voice", rows: 5 },
  { key: "style_traits", label: "Style traits", rows: 5 },
  { key: "paragraph_length", label: "Paragraph length", rows: 1 },
  { key: "subheads", label: "Subheads guidance", rows: 2 },
  { key: "tone_level", label: "Tone level", rows: 1 },
  { key: "good_sentence", label: "Sample good sentence", rows: 3 },
  { key: "avoid_with", label: "Avoid with this writer", rows: 3 },
];

function Section({ title, defaultOpen = false, right, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white border border-[var(--border)] mb-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 sm:px-5 py-3 border-b border-[var(--border)]">
        <button
          onClick={() => setOpen(!open)}
          className="flex-1 flex items-center gap-2 hover:opacity-80 text-left"
        >
          {open ? (
            <ChevronDown className="w-4 h-4 text-[var(--ink-muted)]" />
          ) : (
            <ChevronRight className="w-4 h-4 text-[var(--ink-muted)]" />
          )}
          <span className="font-serif-display text-base">{title}</span>
        </button>
        {right && <div className="sm:shrink-0">{right}</div>}
      </div>
      {open && <div className="p-4 sm:p-5">{children}</div>}
    </div>
  );
}

function TextareaField({ label, value, onChange, rows = 4, testId }) {
  return (
    <label className="block mb-4">
      <span className="overline block mb-1.5">{label}</span>
      <textarea
        data-testid={testId}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        className="w-full border border-[var(--border)] px-3 py-2 text-sm leading-relaxed outline-none focus:border-[var(--slate-900)] bg-white font-mono-display"
      />
    </label>
  );
}

function wpHost(url) {
  if (!url) return "";
  try {
    return new URL(url).host.replace(/^www\./, "");
  } catch {
    return url.replace(/^https?:\/\//, "").replace(/\/$/, "");
  }
}

function relativeTime(iso) {
  if (!iso) return "never";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleString();
}

// The complete editable subset of settings. Any new savable field must be
// added here — the save payload is derived from this object automatically so
// we can never accidentally drop a key.
const EDITABLE_KEYS = [
  "editorial_brief",
  "news_search_query",
  "research_system_prompt",
  "print_edition_prompt",
  "print_edition_default_words",
  "fact_check_prompt",
  "ad_banner_prompt",
  "fact_check_web_search",
  "fact_check_max_searches",
  "source_note_template",
  "wp_url",
  "wp_username",
  "jetpack_site_id",
  "house_rules",
  "writers",
  "sources",
  "google_news_locales",
  "social_prompts",
  "image_prompt_style",
  "card_logo_url",
  "card_backgrounds",
  "card_top_chips",
  "card_headline_tags",
  "card_size",
  "card_size_alt",
  "card_padding",
  "do_not_cover",
  "min_external_links",
  "min_internal_links",
  "internal_links_enabled",
  "wp_index_years",
  "wp_index_auto_refresh",
  "fb_link_in_comment_teaser_suffix",
  "fb_link_in_comment_prefix",
  "comment_rules_facebook",
  "comment_rules_instagram",
  "timezone",
  "social_cascade_delay_minutes",
  // Newsletter / MailerLite
  // NOTE: `mailerlite_api_token` is intentionally NOT in this list —
  // it's write-only and only sent when the editor has typed a fresh
  // value (see saveMutation below), matching the wp_app_password
  // pattern. Including it would wipe the saved token every save.
  "mailerlite_group_id",
  "mailerlite_group_ids",
  "mailerlite_all_subscribers",
  "mailerlite_language",
  "mailerlite_from_name",
  "mailerlite_from_email",
  "newsletter_footer_address",
  "newsletter_primary_color",
  "newsletter_accent_color",
  "newsletter_bg_color",
  "newsletter_subject_prompt",
  "newsletter_preheader_prompt",
  "newsletter_intro_prompt",
  "newsletter_theme",
  "image_legend_style",
];

const emptyDraft = () => ({
  editorial_brief: "",
  news_search_query: "",
  research_system_prompt: "",
  print_edition_prompt: "",
  print_edition_default_words: 500,
  fact_check_prompt: "",
  ad_banner_prompt: "",
  fact_check_web_search: true,
  fact_check_max_searches: 6,
  source_note_template: "",
  wp_url: "",
  wp_username: "",
  wp_app_password: "",
  jetpack_site_id: "",
  house_rules: {},
  writers: {},
  sources: [],
  google_news_locales: ["es_mx", "en_mx", "en_us"],
  social_prompts: { facebook: "", instagram: "", linkedin: "" },
  image_prompt_style: "",
  card_logo_url: null,
  card_backgrounds: [],
  card_top_chips: [],
  card_headline_tags: [],
  card_size: { width: 1080, height: 1920 },
  card_size_alt: { width: 1080, height: 1350 },
  card_padding: { top: 96, right: 96, bottom: 96, left: 96 },
  do_not_cover: [],
  min_external_links: 3,
  min_internal_links: 2,
  internal_links_enabled: true,
  wp_index_years: 3,
  wp_index_auto_refresh: true,
  fb_link_in_comment_teaser_suffix: "",
  fb_link_in_comment_prefix: "",
  comment_rules_facebook: "",
  comment_rules_instagram: "",
  timezone: "America/Tijuana",
  social_cascade_delay_minutes: 15,
  // Newsletter / MailerLite
  mailerlite_api_token: "",  // write-only; never surfaced by API
  mailerlite_group_id: "",
  mailerlite_group_ids: [],
  mailerlite_all_subscribers: false,
  mailerlite_language: "en",
  mailerlite_from_name: "",
  mailerlite_from_email: "",
  newsletter_footer_address: "",
  newsletter_primary_color: "",
  newsletter_accent_color: "",
  newsletter_bg_color: "",
  newsletter_subject_prompt: "",
  newsletter_preheader_prompt: "",
  newsletter_intro_prompt: "",
  newsletter_theme: {},
  image_legend_style: {},
});

function draftFromSettings(settings) {
  if (!settings) return emptyDraft();
  return {
    editorial_brief: settings.editorial_brief || "",
    news_search_query: settings.news_search_query || "",
    research_system_prompt: settings.research_system_prompt || "",
    print_edition_prompt: settings.print_edition_prompt || "",
    print_edition_default_words:
      Number(settings.print_edition_default_words) || 500,
    fact_check_prompt: settings.fact_check_prompt || "",
    ad_banner_prompt: settings.ad_banner_prompt || "",
    // Absent means ON — the shipped default. `!== false` keeps an
    // explicit opt-out while treating undefined as enabled.
    fact_check_web_search: settings.fact_check_web_search !== false,
    fact_check_max_searches:
      Number(settings.fact_check_max_searches) || 6,
    source_note_template: settings.source_note_template || "",
    wp_url: settings.wp_url || "",
    wp_username: settings.wp_username || "",
    wp_app_password: "",  // Password is write-only; never surfaced by API
    jetpack_site_id: settings.jetpack_site_id || "",
    house_rules: { ...(settings.house_rules || {}) },
    writers: { ...(settings.writers || {}) },
    sources: [...(settings.sources || [])],
    google_news_locales: [
      ...(settings.google_news_locales || ["es_mx", "en_mx", "en_us"]),
    ],
    social_prompts: {
      facebook: (settings.social_prompts || {}).facebook || "",
      instagram: (settings.social_prompts || {}).instagram || "",
      linkedin: (settings.social_prompts || {}).linkedin || "",
    },
    image_prompt_style: settings.image_prompt_style || "",
    card_logo_url: settings.card_logo_url || null,
    card_backgrounds: [...(settings.card_backgrounds || [])],
    card_top_chips: [...(settings.card_top_chips || [])],
    card_headline_tags: [...(settings.card_headline_tags || [])],
    card_size: {
      width: settings.card_size?.width || 1080,
      height: settings.card_size?.height || 1920,
    },
    card_size_alt: {
      width: settings.card_size_alt?.width || 1080,
      height: settings.card_size_alt?.height || 1350,
    },
    card_padding: {
      top: settings.card_padding?.top ?? 96,
      right: settings.card_padding?.right ?? 96,
      bottom: settings.card_padding?.bottom ?? 96,
      left: settings.card_padding?.left ?? 96,
    },
    do_not_cover: [...(settings.do_not_cover || [])],
    min_external_links: Number.isFinite(settings.min_external_links)
      ? settings.min_external_links
      : 3,
    min_internal_links: Number.isFinite(settings.min_internal_links)
      ? settings.min_internal_links
      : 2,
    internal_links_enabled:
      settings.internal_links_enabled !== false,
    wp_index_years: Number.isFinite(settings.wp_index_years)
      ? settings.wp_index_years
      : 3,
    wp_index_auto_refresh: settings.wp_index_auto_refresh !== false,
    fb_link_in_comment_teaser_suffix:
      settings.fb_link_in_comment_teaser_suffix || "",
    fb_link_in_comment_prefix: settings.fb_link_in_comment_prefix || "",
    comment_rules_facebook: settings.comment_rules_facebook || "",
    comment_rules_instagram: settings.comment_rules_instagram || "",
    timezone: settings.timezone || "America/Tijuana",
    social_cascade_delay_minutes:
      settings.social_cascade_delay_minutes ?? 15,
    // Newsletter — token is write-only. `_set` boolean tells us if
    // one is already saved so the UI can show "Connected" instead
    // of an empty field.
    mailerlite_api_token: "",
    mailerlite_api_token_set: !!settings.mailerlite_api_token_set,
    mailerlite_group_id: settings.mailerlite_group_id || "",
    mailerlite_group_ids: settings.mailerlite_group_ids || [],
    mailerlite_all_subscribers: !!settings.mailerlite_all_subscribers,
    mailerlite_language: settings.mailerlite_language || "en",
    mailerlite_from_name: settings.mailerlite_from_name || "",
    mailerlite_from_email: settings.mailerlite_from_email || "",
    newsletter_footer_address: settings.newsletter_footer_address || "",
    newsletter_primary_color: settings.newsletter_primary_color || "",
    newsletter_accent_color: settings.newsletter_accent_color || "",
    newsletter_bg_color: settings.newsletter_bg_color || "",
    newsletter_subject_prompt: settings.newsletter_subject_prompt || "",
    newsletter_preheader_prompt: settings.newsletter_preheader_prompt || "",
    newsletter_intro_prompt: settings.newsletter_intro_prompt || "",
    // Fully-resolved theme from the API so every design field shows
    // its effective value; saved back whole on Save.
    newsletter_theme: settings.newsletter_theme_effective || {},
    // Disclosure legend look — fully resolved, saved back whole.
    image_legend_style: settings.image_legend_style_effective || {},
  };
}

export default function SettingsPage() {
  const qc = useQueryClient();
  const { data: settings, isLoading } = useQuery({
    queryKey: ["settings"],
    queryFn: api.getSettings,
  });

  const [draft, setDraft] = useState(emptyDraft);
  const [dirty, setDirty] = useState(false);
  const [catFilter, setCatFilter] = useState("");

  useEffect(() => {
    if (settings) {
      setDraft(draftFromSettings(settings));
      setDirty(false);
    }
  }, [settings]);

  // Convenience accessors — read-only views over draft
  const houseRules = draft.house_rules;
  const writers = draft.writers;
  const sources = draft.sources;
  const minExternalLinks = draft.min_external_links;

  const wpCategories = settings?.wordpress_categories || [];
  const wpAuthors = settings?.wordpress_authors || [];

  // Single mutator surface — every edit funnels through this so `dirty`
  // tracking and payload composition stay in lock-step.
  const patchDraft = (patch) => {
    setDraft((prev) => ({ ...prev, ...patch }));
    setDirty(true);
  };

  const updateHouseRule = (k, v) =>
    patchDraft({ house_rules: { ...draft.house_rules, [k]: v } });

  const updateWriterField = (writerKey, field, v) =>
    patchDraft({
      writers: {
        ...draft.writers,
        [writerKey]: { ...(draft.writers[writerKey] || {}), [field]: v },
      },
    });

  const slugify = (s) =>
    (s || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_|_$/g, "");

  const addWriter = ({ name, wordpressAuthorId }) => {
    const trimmed = (name || "").trim();
    if (trimmed.length < 2) {
      toast.error("Writer name must be at least 2 characters");
      return false;
    }
    let base = slugify(trimmed) || "writer";
    let key = base;
    let i = 2;
    while (draft.writers[key]) {
      key = `${base}_${i++}`;
    }
    patchDraft({
      writers: {
        ...draft.writers,
        [key]: {
          name: trimmed,
          voice: "",
          best_for: "",
          style_traits: "",
          paragraph_length: "",
          subheads: "",
          tone_level: "",
          good_sentence: "",
          avoid_with: "",
          assignment_tags: [],
          wordpress_author_id:
            wordpressAuthorId ? parseInt(wordpressAuthorId, 10) : null,
        },
      },
    });
    toast.success(`Added "${trimmed}" — remember to Save`);
    return true;
  };

  const removeWriter = (writerKey) => {
    if (
      !window.confirm(
        "Remove this writer? Articles already written by them stay intact."
      )
    ) return;
    const next = { ...draft.writers };
    delete next[writerKey];
    patchDraft({ writers: next });
  };

  const setMinExternalLinks = (v) => patchDraft({ min_external_links: v });

  const saveMutation = useMutation({
    // Payload is derived from `draft` — impossible to accidentally omit a key.
    mutationFn: () => {
      const payload = {};
      for (const k of EDITABLE_KEYS) payload[k] = draft[k];
      // Only include wp_app_password when the editor typed a new one
      // (backend keeps the existing value when the key is absent).
      if (draft.wp_app_password) {
        payload.wp_app_password = draft.wp_app_password;
      }
      // Same pattern for the MailerLite API token — only send when the
      // editor typed a new value, otherwise leave the saved token in
      // place. Empty string would wipe it.
      if (draft.mailerlite_api_token) {
        payload.mailerlite_api_token = draft.mailerlite_api_token;
      }
      return api.updateSettings(payload);
    },
    onSuccess: () => {
      toast.success("Settings saved");
      setDirty(false);
      qc.invalidateQueries({ queryKey: ["settings"] });
      qc.invalidateQueries({ queryKey: ["writers"] });
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const syncMutation = useMutation({
    mutationFn: () => api.syncWordpress(),
    onSuccess: (data) => {
      const parts = [];
      if (data.categories_error) {
        parts.push(`categories failed: ${data.categories_error.slice(0, 120)}`);
      } else {
        parts.push(`${data.categories_count} categories`);
      }
      if (data.authors_error) {
        parts.push(`authors failed: ${data.authors_error.slice(0, 120)}`);
      } else {
        parts.push(`${data.authors_count} authors`);
      }
      if (data.categories_error || data.authors_error) {
        toast.warning(`Partial sync — ${parts.join(" · ")}`);
      } else {
        toast.success(`Synced ${parts.join(" · ")}`);
      }
      qc.invalidateQueries({ queryKey: ["settings"] });
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || "Sync failed"),
  });

  const handleReset = () => {
    if (!settings) return;
    if (!window.confirm("Discard unsaved changes?")) return;
    setDraft(draftFromSettings(settings));
    setDirty(false);
  };

  const updateSource = (idx, patch) =>
    patchDraft({
      sources: draft.sources.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
    });

  const removeSource = (idx) =>
    patchDraft({ sources: draft.sources.filter((_, i) => i !== idx) });

  const addSource = (src) =>
    patchDraft({ sources: [...draft.sources, { ...src, enabled: true }] });

  if (isLoading || !settings) {
    return (
      <div className="p-10 flex items-center gap-2 text-[var(--ink-muted)]">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading settings…
      </div>
    );
  }

  const filteredCats = wpCategories.filter((c) =>
    (c.name || "").toLowerCase().includes(catFilter.trim().toLowerCase())
  );

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-[var(--border)] bg-white sticky top-0 z-10">
        <div className="px-4 sm:px-10 py-4 sm:py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <div className="overline">Newsroom · Settings</div>
            <h1 className="font-serif-display text-2xl sm:text-3xl tracking-tight leading-tight mt-1">
              Editorial Style Brief
            </h1>
            <p className="text-xs text-[var(--ink-muted)] mt-1 hidden sm:block">
              Everything here is injected into every research and article
              generation. Changes save to the database and take effect
              immediately.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid={SETTINGS.resetButton}
              onClick={handleReset}
              disabled={!dirty}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-[var(--border)] hover:bg-[var(--secondary)] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Discard
            </button>
            <button
              data-testid={SETTINGS.saveButton}
              onClick={() => saveMutation.mutate()}
              disabled={!dirty || saveMutation.isPending}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saveMutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Save className="w-3.5 h-3.5" />
              )}
              Save changes
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        <div className="px-4 sm:px-10 py-6">
          {/* Security */}
          <Section title="Security · Two-Factor Authentication" defaultOpen={false}>
            <TwoFactorSettings />
          </Section>

          {/* Newsroom timezone — used to interpret editor-picked
              schedule times AND to display every timestamp on this
              property. Property-scoped so a multi-property user gets
              a different clock per newsroom. */}
          <Section title="Newsroom · Timezone" defaultOpen={false}>
            <p className="text-xs text-[var(--ink-muted)] mb-3">
              Every schedule picker and timestamp on this property is
              interpreted in the timezone chosen here. Change it and
              the queue on <em>Scheduled Tasks</em> re-renders in the
              new zone without moving the actual publish moments.
            </p>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 flex-wrap">
              <select
                data-testid="settings-timezone-select"
                value={draft.timezone || "America/Tijuana"}
                onChange={(e) => {
                  setDraft({ ...draft, timezone: e.target.value });
                  setDirty(true);
                }}
                className="px-3 py-2 text-sm bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)] min-w-[280px]"
              >
                {COMMON_TIMEZONES.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </select>
              <div
                data-testid="settings-timezone-preview"
                className="text-xs font-mono-display text-[var(--ink-muted)]"
              >
                It&rsquo;s currently{" "}
                <span className="text-[var(--ink)]">
                  {formatInTz(
                    new Date().toISOString(),
                    draft.timezone || "America/Tijuana",
                    "compact",
                  )}
                </span>{" "}
                <span className="opacity-70">
                  ({tzAbbreviation(draft.timezone || "America/Tijuana")})
                </span>
              </div>
            </div>
            <p className="text-[11px] text-[var(--ink-muted)] mt-2">
              Stored timestamps stay in UTC on the server &mdash; only the
              display and the interpretation of picker input changes.
            </p>
          </Section>

          {/* Prompts backup — export every prompt as one JSON file,
              import a (partial) file, or reset to the shipped defaults.
              Import/reset preview the changes before saving. */}
          <Section title="Prompts · Export, import & reset" defaultOpen={false}>
            <PromptBackupSection dirty={dirty} propertyName={settings?.name || ""} />
          </Section>

          {/* Nightly backups of this property to a private R2 bucket —
              status, history and (admins) "Back up now". */}
          <Section title="Backups · Disaster recovery" defaultOpen={false}>
            <BackupsSection timezone={draft.timezone} />
          </Section>

          {/* Article generation rules */}
          <Section title="Article generation · Publishing rules" defaultOpen={false}>
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="min-ext-links"
                  className="overline text-[var(--ink-muted)] block mb-1.5"
                >
                  Minimum outbound source links per article
                </label>
                <div className="flex items-center gap-3">
                  <input
                    id="min-ext-links"
                    data-testid="settings-min-external-links"
                    type="number"
                    min={0}
                    max={15}
                    value={minExternalLinks}
                    onChange={(e) => {
                      const n = parseInt(e.target.value, 10);
                      setMinExternalLinks(Number.isFinite(n) ? n : 0);
                    }}
                    className="w-24 border border-[var(--border)] px-3 py-1.5 text-sm text-center focus:outline-none focus:border-[var(--slate-900)]"
                  />
                  <span className="text-xs text-[var(--ink-muted)]">
                    inline &lt;a href&gt; links (0–15). Set to 0 to make outbound
                    links optional.
                  </span>
                </div>
              </div>
            </div>
          </Section>

          {/* Internal-link index over the WordPress archive */}
          <Section
            title="WordPress · Internal Link Index (SEO)"
            defaultOpen={false}
          >
            <InternalLinkIndexSettings draft={draft} patchDraft={patchDraft} />
          </Section>

          {/* WordPress Sync */}
          <Section
            title="WordPress · Categories & Authors"
            defaultOpen={false}
            right={
              <button
                data-testid={SETTINGS.syncWpButton}
                onClick={() => syncMutation.mutate()}
                disabled={syncMutation.isPending}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[var(--slate-900)] text-white hover:bg-black disabled:opacity-60"
              >
                {syncMutation.isPending ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <RefreshCw className="w-3.5 h-3.5" />
                )}
                Sync from WordPress
              </button>
            }
          >
            <div className="text-sm mb-4 grid grid-cols-2 gap-y-2">
              <div className="text-[var(--ink-muted)]">Site</div>
              <div className="font-mono-display" data-testid="wp-connected-site">
                {wpHost(settings.wp_url) || (
                  <span className="italic text-[var(--ink-muted)]">
                    not configured
                  </span>
                )}
              </div>
              <div className="text-[var(--ink-muted)]">Categories synced</div>
              <div className="font-mono-display">{wpCategories.length}</div>
              <div className="text-[var(--ink-muted)]">Authors synced</div>
              <div className="font-mono-display">{wpAuthors.length}</div>
              <div className="text-[var(--ink-muted)]">Last sync</div>
              <div className="font-mono-display">
                {relativeTime(settings.wordpress_last_sync)}
              </div>
            </div>

            {wpCategories.length > 0 && (
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="overline flex items-center gap-1.5">
                    <Tag className="w-3 h-3" /> Available categories
                  </span>
                  <input
                    value={catFilter}
                    onChange={(e) => setCatFilter(e.target.value)}
                    placeholder="Filter…"
                    className="px-2 py-1 text-xs border border-[var(--border)] outline-none focus:border-[var(--slate-900)] w-40 font-mono-display"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1 max-h-72 overflow-auto border border-[var(--border)] p-2 bg-[var(--bg)]">
                  {filteredCats.map((c) => (
                    <div
                      key={c.id}
                      data-testid={`${SETTINGS.categoryItem}-${c.id}`}
                      className="text-xs px-2 py-1 bg-white border border-[var(--border)] flex items-center justify-between gap-2"
                    >
                      <span className="truncate" title={c.name}>
                        {c.name}
                      </span>
                      <span className="text-[10px] text-[var(--ink-muted)] font-mono-display shrink-0">
                        {c.count}
                      </span>
                    </div>
                  ))}
                  {filteredCats.length === 0 && (
                    <div className="col-span-full text-xs text-[var(--ink-muted)] italic p-4 text-center">
                      No categories match &ldquo;{catFilter}&rdquo;
                    </div>
                  )}
                </div>
              </div>
            )}

            {wpCategories.length === 0 && (
              <div className="text-sm text-[var(--ink-muted)] italic border border-dashed border-[var(--border)] p-4 text-center">
                No WordPress categories yet. Click &ldquo;Sync from WordPress&rdquo; above
                to pull the current list from{" "}
                {wpHost(settings.wp_url) || "your WordPress site"}.
              </div>
            )}
          </Section>

          {/* Editorial brief — used as the LLM system prompt when running research */}
          <div className="mt-2 mb-2 overline">Editorial brief</div>
          <Section
            title="Editor's default research brief"
            defaultOpen={false}
          >
            <p className="text-xs text-[var(--ink-muted)] mb-3">
              Sent as the standing instruction every time you press{" "}
              <em>Run research</em>. Per-run guidelines on the Research page
              are appended to this brief.
            </p>
            <textarea
              data-testid={`${SETTINGS.houseRuleField}-editorial_brief`}
              value={draft.editorial_brief}
              onChange={(e) => {
                setDraft({ ...draft, editorial_brief: e.target.value });
                setDirty(true);
              }}
              rows={10}
              className="w-full resize-y px-3 py-2 text-sm bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)]"
              placeholder="Define what THIS newspaper is looking for — beats, audience, geographies, tone, etc."
            />

            {/* Short keyword query — used ONLY for Google News + site-filter
                sources. The full editorial brief above goes to the LLM as
                a system prompt; if we sent the brief to Google News it
                would return 0 results (Google News RSS treats long
                queries as a literal phrase search). */}
            <div className="mt-4">
              <label className="block text-xs uppercase tracking-wider font-mono-display text-[var(--ink-muted)] mb-1">
                News search query
              </label>
              <input
                type="text"
                data-testid="news-search-query-input"
                value={draft.news_search_query}
                onChange={(e) => {
                  setDraft({ ...draft, news_search_query: e.target.value });
                  setDirty(true);
                }}
                placeholder='e.g. "Los Cabos Baja California Sur"'
                className="w-full px-3 py-2 text-sm bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)]"
              />
              <p className="text-[11px] text-[var(--ink-muted)] mt-1.5 leading-snug">
                Short keyword string sent to Google News + site-filter
                sources. Keep it under ~8 words — Google News RSS ignores
                anything longer. Blank = fall back to the property name.
              </p>
            </div>
          </Section>

          {/* Advanced: LLM system prompt override. Editors who want
              tighter control (or a totally different research brief
              shape) can replace the built-in template. Left blank =
              the default template ships. Supports the placeholders
              documented inline. */}
          <Section
            title="Research LLM system prompt (advanced)"
            defaultOpen={false}
          >
            <p className="text-xs text-[var(--ink-muted)] mb-3">
              Optional. If left blank, Newsdesk uses a geography-neutral
              default that reads your Editorial brief as the source of
              truth. Only override this if you know what you&apos;re doing
              &mdash; a bad template will produce bad ideas across every
              run.
            </p>
            <p className="text-[11px] text-[var(--ink-muted)] mb-2 font-mono-display">
              Supported placeholders (curly braces): {"{property_name}"},{" "}
              {"{editorial_brief}"}, {"{house_rules}"}, {"{writer_guide}"},{" "}
              {"{writer_enum}"}.
            </p>
            <textarea
              data-testid="research-system-prompt-input"
              value={draft.research_system_prompt}
              onChange={(e) => {
                setDraft({ ...draft, research_system_prompt: e.target.value });
                setDirty(true);
              }}
              rows={14}
              spellCheck={false}
              className="w-full resize-y px-3 py-2 text-xs font-mono-display bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)]"
              placeholder="Leave blank to use Newsdesk's default template."
            />
            {draft.research_system_prompt && (
              <button
                type="button"
                data-testid="research-system-prompt-reset"
                onClick={() => {
                  setDraft({ ...draft, research_system_prompt: "" });
                  setDirty(true);
                }}
                className="mt-2 text-[11px] text-red-600 hover:text-red-800 underline"
              >
                Reset to default template
              </button>
            )}
          </Section>

          {/* Print Edition — condense template + default word count.
              Consumed by the Convert-to-Print workflow. */}
          <Section
            title="Print edition · condense template & word count"
            defaultOpen={false}
          >
            <p className="text-xs text-[var(--ink-muted)] mb-3">
              Used by <em>Convert to Print</em> to shrink Library
              articles down for the printed edition. The default
              template is pre-loaded below and always kept in sync —
              if you clear the box completely and save, Newsdesk will
              restore the shipped default automatically.
            </p>
            <label className="block text-xs text-[var(--ink-muted)] mb-1">
              Default word count per article
            </label>
            <input
              data-testid="print-default-words-input"
              type="number"
              min={80}
              max={2500}
              step={25}
              value={draft.print_edition_default_words}
              onChange={(e) => {
                setDraft({
                  ...draft,
                  print_edition_default_words: Number(e.target.value) || 500,
                });
                setDirty(true);
              }}
              className="w-40 px-3 py-2 text-sm bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)]"
            />
            <p className="text-[10px] text-[var(--ink-muted)] mt-1 mb-4">
              Pre-fills the word-count field on the Convert to Print page.
              Editors can still override per batch.
            </p>

            <label className="block text-xs text-[var(--ink-muted)] mb-1">
              Condense prompt template (LLM system prompt)
            </label>
            <p className="text-[11px] text-[var(--ink-muted)] mb-2 font-mono-display">
              Supported placeholders: {"{property_name}"}, {"{word_count}"}.
              The output MUST be JSON with{" "}
              <code className="font-mono-display">headline</code> and{" "}
              <code className="font-mono-display">body</code> keys.
            </p>
            <textarea
              data-testid="print-prompt-input"
              value={draft.print_edition_prompt}
              onChange={(e) => {
                setDraft({ ...draft, print_edition_prompt: e.target.value });
                setDirty(true);
              }}
              rows={14}
              spellCheck={false}
              className="w-full resize-y px-3 py-2 text-xs font-mono-display bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)]"
              placeholder="Leave blank to use Newsdesk's default template."
            />
            {draft.print_edition_prompt !== (settings?.print_edition_prompt_default || "") && (
              <button
                type="button"
                data-testid="print-prompt-reset"
                onClick={() => {
                  setDraft({
                    ...draft,
                    print_edition_prompt:
                      settings?.print_edition_prompt_default || "",
                  });
                  setDirty(true);
                }}
                className="mt-2 text-[11px] text-red-600 hover:text-red-800 underline"
              >
                Reset to default template
              </button>
            )}
          </Section>

          {/* Fact Check — LLM prompt template used by the "Fact check"
              button on the Article page. Blank restores the shipped
              default automatically. */}
          <Section
            title="Fact check · LLM prompt"
            defaultOpen={false}
          >
            <p className="text-xs text-[var(--ink-muted)] mb-3">
              Used by the <em>Fact check</em> button in the article view.
              The LLM receives the article body and its{" "}
              <code className="font-mono-display">sources_used</code>{" "}
              list, then returns a 0-100 score and a bullet list of
              flagged claims. Every run is appended to the article&apos;s
              history — re-running never overwrites previous checks.
              Editors pick which model runs this task on the{" "}
              <a
                href="/admin/llm-models"
                className="text-[var(--accent)] underline"
              >
                LLM Models
              </a>{" "}
              admin page.
            </p>
            <p className="text-[11px] text-[var(--ink-muted)] mb-2 font-mono-display">
              Supported placeholders: {"{property_name}"},{" "}
              {"{article_body}"}, {"{sources_json}"},{" "}
              {"{reference_date}"}, {"{calendar_reference}"}. Output MUST
              be JSON with{" "}
              <code className="font-mono-display">flagged_claims</code>{" "}
              (array of{" "}
              {"{claim, comment, verdict, source_url, basis}"}) and{" "}
              <code className="font-mono-display">score</code>{" "}
              (integer 0-100). Keep that order: the model writes left to
              right, so reasoning (comment) must come before the verdict.
            </p>
            <p className="text-xs text-[var(--ink-muted)] mb-3">
              <strong>Dates.</strong> {"{reference_date}"} is the
              article&apos;s publish (or scheduled) date in this
              property&apos;s timezone, and {"{calendar_reference}"} is a
              weekday lookup table covering 30 days before it to 120
              days after. Together they let the checker resolve
              &ldquo;this Thursday&rdquo; to a real date and catch a date
              paired with the wrong weekday — models get that arithmetic
              wrong on their own. If your template leaves these
              placeholders out, Newsdesk appends a date reference to the
              end automatically, so date checking still runs.
            </p>
            <p className="text-xs text-[var(--ink-muted)] mb-3">
              <strong>Social posts and proposed fixes.</strong> Newsdesk
              always appends a short block to the end of this prompt. It
              adds the article&apos;s Facebook and Instagram copy for
              checking, and asks for a{" "}
              <code className="font-mono-display">location</code>{" "}
              (article / facebook / instagram) plus an{" "}
              <code className="font-mono-display">original</code> and{" "}
              <code className="font-mono-display">proposed</code> passage
              on every partial, unsupported or contradicted claim, so the
              editor can accept the fix with one click. You don&apos;t need to
              add anything here for that to work.
            </p>

            {/* Web search — verify claims the article's own sources
                don't cover. Anthropic-routed models only. */}
            <label className="flex items-start gap-2 mb-2 cursor-pointer">
              <input
                type="checkbox"
                data-testid="fact-check-web-search-toggle"
                checked={draft.fact_check_web_search}
                onChange={(e) => {
                  setDraft({
                    ...draft,
                    fact_check_web_search: e.target.checked,
                  });
                  setDirty(true);
                }}
                className="mt-0.5"
              />
              <span className="text-xs">
                <strong>Check claims against the web.</strong> Lets the
                fact-checker verify details the article&apos;s sources
                don&apos;t cover, re-check claims whose source
                couldn&apos;t be scraped, and catch facts that changed
                after the sources were written (a postponed event, a new
                officeholder). Adds about{" "}
                {"$"}
                {(0.01 * (Number(draft.fact_check_max_searches) || 6)).toFixed(2)}{" "}
                per run at most, on top of tokens. Only applies when the
                Fact check task is routed to a Claude model on the{" "}
                <a
                  href="/admin/llm-models"
                  className="text-[var(--accent)] underline"
                >
                  LLM Models
                </a>{" "}
                page — other providers run without it.
              </span>
            </label>
            {draft.fact_check_web_search && (
              <label className="flex items-center gap-2 mb-3 text-xs">
                <span>Max searches per run</span>
                <input
                  type="number"
                  min={1}
                  max={20}
                  data-testid="fact-check-max-searches-input"
                  value={draft.fact_check_max_searches}
                  onChange={(e) => {
                    setDraft({
                      ...draft,
                      fact_check_max_searches: e.target.value,
                    });
                    setDirty(true);
                  }}
                  className="w-20 px-2 py-1 font-mono-display bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)]"
                />
                <span className="text-[var(--ink-muted)]">
                  1-20. The prompt tells the checker to spend them on
                  event dates and unverified claims first.
                </span>
              </label>
            )}
            <textarea
              data-testid="fact-check-prompt-input"
              value={draft.fact_check_prompt}
              onChange={(e) => {
                setDraft({ ...draft, fact_check_prompt: e.target.value });
                setDirty(true);
              }}
              rows={14}
              spellCheck={false}
              className="w-full resize-y px-3 py-2 text-xs font-mono-display bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)]"
              placeholder="Leave blank to use Newsdesk's default template."
            />
            {draft.fact_check_prompt !== (settings?.fact_check_prompt_default || "") && (
              <button
                type="button"
                data-testid="fact-check-prompt-reset"
                onClick={() => {
                  setDraft({
                    ...draft,
                    fact_check_prompt:
                      settings?.fact_check_prompt_default || "",
                  });
                  setDirty(true);
                }}
                className="mt-2 text-[11px] text-red-600 hover:text-red-800 underline"
              >
                Reset to default template
              </button>
            )}
          </Section>

          {/* Sponsor banner builder — the art-direction prompt behind
              Sponsor ads → Edit styles → Create banner. */}
          <Section
            title="Sponsor ads · banner prompt"
            defaultOpen={false}
          >
            <p className="text-xs text-[var(--ink-muted)] mb-3">
              Used by <em>Create banner</em> in the sponsor ad Style
              Editor. It tells the AI how to turn a sponsor&apos;s text
              and photos, or a finished ad, into a 1300&nbsp;×&nbsp;200
              banner: how long the headline may be, what to put in the
              button, how to choose colours, a layout and a font. The AI
              only writes the wording and the design choices — Newsdesk
              draws the banner itself, so names and phone numbers are
              never misspelled.
            </p>
            <p className="text-[11px] text-[var(--ink-muted)] mb-2 font-mono-display">
              Placeholder: <span className="text-[var(--ink)]">{"{property_name}"}</span>
            </p>
            <p className="text-xs text-[var(--ink-muted)] mb-3">
              Newsdesk always adds the answer format to the end of this
              prompt, so you can rewrite everything here without
              breaking the banner builder. This prompt is included in
              the prompts export / import file.
            </p>
            <textarea
              data-testid="ad-banner-prompt-input"
              value={draft.ad_banner_prompt}
              onChange={(e) => {
                setDraft({ ...draft, ad_banner_prompt: e.target.value });
                setDirty(true);
              }}
              rows={14}
              spellCheck={false}
              className="w-full resize-y px-3 py-2 text-xs font-mono-display bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)]"
              placeholder="Leave blank to use Newsdesk's default prompt."
            />
            {draft.ad_banner_prompt !== (settings?.ad_banner_prompt_default || "") && (
              <button
                type="button"
                data-testid="ad-banner-prompt-reset"
                onClick={() => {
                  setDraft({
                    ...draft,
                    ad_banner_prompt:
                      settings?.ad_banner_prompt_default || "",
                  });
                  setDirty(true);
                }}
                className="mt-2 text-[11px] text-red-600 hover:text-red-800 underline"
              >
                Reset to default prompt
              </button>
            )}
          </Section>

          {/* Source note — attribution paragraph template used by the
              "Add source note" button on the article page. */}
          <Section
            title="Source note · template"
            defaultOpen={false}
          >
            <p className="text-xs text-[var(--ink-muted)] mb-3">
              Used by the <em>Add source note</em> button in the article
              view&apos;s <em>Sources consulted by the writer</em> section.
              The rendered paragraph is appended as the final paragraph of
              the article body and renders one measure smaller than body
              text.
            </p>
            <p className="text-[11px] text-[var(--ink-muted)] mb-2 font-mono-display">
              Supported placeholders: {"{source_name}"}, {"{property_name}"}.
              {" "}
              {"{source_name}"} expands to every source on the article,
              comma-separated and hyperlinked to its source URL.
            </p>
            <SourceNoteTemplateField
              value={draft.source_note_template}
              onChange={(v) => {
                setDraft({ ...draft, source_note_template: v });
                setDirty(true);
              }}
              defaultTemplate={settings?.source_note_template_default || ""}
            />
          </Section>

          <Section
            title={`Do not cover · ${draft.do_not_cover.length}`}
            defaultOpen={false}
          >
            <p className="text-xs text-[var(--ink-muted)] mb-3">
              Story headlines listed here are passed to the ideation LLM as a
              hard exclusion — it will skip these stories, their translations,
              and any rewordings. Add entries from the &ldquo;Do not cover&rdquo;
              button on the Story ideas list. Remove an entry to allow the
              story to surface again on future research runs.
            </p>
            {draft.do_not_cover.length === 0 ? (
              <div
                data-testid="do-not-cover-empty"
                className="text-sm text-[var(--ink-muted)] italic border border-dashed border-[var(--border)] p-4 text-center"
              >
                Nothing on the exclusion list yet.
              </div>
            ) : (
              <ul
                data-testid="do-not-cover-list"
                className="divide-y divide-[var(--border)] border border-[var(--border)]"
              >
                {draft.do_not_cover.map((title, i) => (
                  <li
                    key={`${title}-${i}`}
                    data-testid={`do-not-cover-item-${i}`}
                    className="flex items-start justify-between gap-3 px-3 py-2 bg-white"
                  >
                    <span className="text-sm leading-snug flex-1 min-w-0 break-words">
                      {title}
                    </span>
                    <button
                      data-testid={`do-not-cover-remove-${i}`}
                      onClick={() => {
                        const next = draft.do_not_cover.filter(
                          (_, j) => j !== i,
                        );
                        setDraft({ ...draft, do_not_cover: next });
                        setDirty(true);
                      }}
                      className="shrink-0 inline-flex items-center gap-1 text-xs text-[var(--ink-muted)] hover:text-red-700 px-2 py-1 border border-transparent hover:border-red-200 hover:bg-red-50"
                      title="Remove from Do-not-cover list"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          {/* Social media prompts — per-property, per-network */}
          <div className="mt-6 mb-2 overline">Social media</div>

          <Section
            title="Social cascade delay"
            defaultOpen={false}
          >
            <p className="text-xs text-[var(--ink-muted)] mb-3">
              When you schedule the WordPress article for a future publish,
              the FB, Instagram and FB Story pickers pre-fill their schedule
              time to <span className="font-mono">WP publish + N minutes</span>{" "}
              so the socials fire slightly after the article is live. Set{" "}
              <span className="font-mono">N</span> here (0 – 240).
            </p>
            <div className="flex items-center gap-2">
              <input
                data-testid="settings-social-cascade-delay"
                type="number"
                min="0"
                max="240"
                step="1"
                value={draft.social_cascade_delay_minutes ?? 15}
                onChange={(e) => {
                  const v = parseInt(e.target.value || "15", 10);
                  setDraft({
                    ...draft,
                    social_cascade_delay_minutes: Number.isFinite(v) ? v : 15,
                  });
                  setDirty(true);
                }}
                className="w-24 px-3 py-2 text-sm bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)] font-mono-display"
              />
              <span className="text-sm text-[var(--ink-muted)]">
                minutes after WP publish
              </span>
            </div>
          </Section>

          <Section
            title="Facebook · Instagram · LinkedIn generation prompts"
            defaultOpen={false}
          >
            <p className="text-xs text-[var(--ink-muted)] mb-3">
              These instructions tell the article writer how to generate the
              social copy for each network. Use{" "}
              <span className="font-mono">{"{site_url}"}</span> and{" "}
              <span className="font-mono">{"{site_name}"}</span> — they&rsquo;re
              replaced with this property&rsquo;s WordPress host and display
              name at generation time.
            </p>
            {[
              {
                key: "facebook",
                label: "Facebook",
                testId: "social-prompt-facebook",
              },
              {
                key: "instagram",
                label: "Instagram",
                testId: "social-prompt-instagram",
              },
              {
                key: "linkedin",
                label: "LinkedIn",
                testId: "social-prompt-linkedin",
              },
            ].map((net) => (
              <div key={net.key} className="mb-4">
                <label className="text-xs uppercase tracking-wider text-[var(--ink-muted)] font-mono-display block mb-1">
                  {net.label}
                </label>
                <textarea
                  data-testid={net.testId}
                  value={draft.social_prompts?.[net.key] || ""}
                  onChange={(e) => {
                    setDraft({
                      ...draft,
                      social_prompts: {
                        ...(draft.social_prompts || {}),
                        [net.key]: e.target.value,
                      },
                    });
                    setDirty(true);
                  }}
                  rows={12}
                  className="w-full resize-y px-3 py-2 text-sm font-mono-display leading-relaxed bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)]"
                  placeholder={`Instructions for the ${net.label} post…`}
                />
              </div>
            ))}
          </Section>

          {/* Facebook "link in the first comment" phrases — used by the
              Publish to Facebook tool when the editor ticks that
              checkbox. Both fields are optional; blank = use the
              app-shipped defaults (defined in routes/social.py). */}
          <Section
            title="Facebook · &ldquo;Link in the first comment&rdquo; phrases"
            defaultOpen={false}
          >
            <p className="text-xs text-[var(--ink-muted)] mb-4">
              These phrases are used when you tick &ldquo;Share link in the
              first comment&rdquo; on the Publish to Facebook tool. Leave
              blank to use the app defaults; edit here to override per
              property.
            </p>
            <div className="mb-4">
              <label className="text-xs uppercase tracking-wider text-[var(--ink-muted)] font-mono-display block mb-1">
                Teaser at the end of the post copy
              </label>
              <input
                data-testid="fb-teaser-suffix-input"
                type="text"
                value={draft.fb_link_in_comment_teaser_suffix || ""}
                onChange={(e) => {
                  setDraft({
                    ...draft,
                    fb_link_in_comment_teaser_suffix: e.target.value,
                  });
                  setDirty(true);
                }}
                className="w-full px-3 py-2 text-sm bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)]"
                placeholder="👇 Link in the comments!"
              />
              <p className="text-[11px] text-[var(--ink-muted)] mt-1">
                Appended (on a new line) to the FB post caption. Default:{" "}
                <span className="font-mono-display">
                  &#128071; Link in the comments!
                </span>
              </p>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[var(--ink-muted)] font-mono-display block mb-1">
                Prefix on the first comment (before the URL)
              </label>
              <input
                data-testid="fb-comment-prefix-input"
                type="text"
                value={draft.fb_link_in_comment_prefix || ""}
                onChange={(e) => {
                  setDraft({
                    ...draft,
                    fb_link_in_comment_prefix: e.target.value,
                  });
                  setDirty(true);
                }}
                className="w-full px-3 py-2 text-sm bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)]"
                placeholder="👉 Read the full story here:"
              />
              <p className="text-[11px] text-[var(--ink-muted)] mt-1">
                Prepended to the article URL inside the first comment.
                Default:{" "}
                <span className="font-mono-display">
                  &#128073; Read the full story here:
                </span>
              </p>
            </div>
          </Section>

          {/* Community management — rules that shape "Assisted Response"
              (AR) drafts on the Social Comments workspace. Blank fields
              fall back to the shipped defaults defined in
              routes/social_comments.py. Editors can override per
              platform to match their community voice. */}
          <Section
            title="Community management · Comment reply rules"
            defaultOpen={false}
          >
            <p className="text-xs text-[var(--ink-muted)] mb-4">
              Rules the LLM follows when drafting Assisted Response
              replies on Social Comments. Leave a field blank to use the
              app-shipped default (tuned for Baja bilingual audiences,
              factual, non-argumentative).
            </p>
            <div className="mb-4">
              <label className="text-xs uppercase tracking-wider text-[var(--ink-muted)] font-mono-display block mb-1">
                Facebook comment rules
              </label>
              <textarea
                data-testid="settings-comment-rules-fb"
                value={draft.comment_rules_facebook || ""}
                onChange={(e) => {
                  setDraft({
                    ...draft,
                    comment_rules_facebook: e.target.value,
                  });
                  setDirty(true);
                }}
                rows={10}
                className="w-full px-3 py-2 text-xs font-mono-display bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)] resize-y"
                placeholder="(Blank → use the shipped default — bilingual, warm, factual, 1-3 sentences, no political opinions.)"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[var(--ink-muted)] font-mono-display block mb-1">
                Instagram comment rules
              </label>
              <textarea
                data-testid="settings-comment-rules-ig"
                value={draft.comment_rules_instagram || ""}
                onChange={(e) => {
                  setDraft({
                    ...draft,
                    comment_rules_instagram: e.target.value,
                  });
                  setDirty(true);
                }}
                rows={10}
                className="w-full px-3 py-2 text-xs font-mono-display bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)] resize-y"
                placeholder="(Blank → use the shipped default — casual, under 200 chars, one emoji, no line breaks.)"
              />
            </div>
          </Section>

          {/* AI image prompt style — appended to the per-article subject */}
          <div className="mt-6 mb-2 overline">AI image prompt style</div>
          <Section
            title="Standardised style suffix for AI-generated images"
            defaultOpen={false}
          >
            <p className="text-xs text-[var(--ink-muted)] mb-3">
              This text is appended to every AI-generated image prompt after
              the article-specific subject. Edit the style, lighting,
              composition, and rendering rules here — the article-specific
              part (headline &amp; deck) is tuned per-article on the article
              page just before you click Generate.
            </p>
            <textarea
              data-testid="image-prompt-style-textarea"
              value={draft.image_prompt_style || ""}
              onChange={(e) => {
                setDraft({ ...draft, image_prompt_style: e.target.value });
                setDirty(true);
              }}
              rows={7}
              className="w-full resize-y px-3 py-2 text-sm font-mono-display leading-relaxed bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)]"
              placeholder="Style: cinematic photojournalism, warm natural light…"
            />
          </Section>

          {/* Look of the legend burnt into published photos */}
          <Section title="Images · Disclosure legend style" defaultOpen={false}>
            <ImageLegendStyleEditor
              value={draft.image_legend_style}
              onChange={(next) => patchDraft({ image_legend_style: next })}
            />
          </Section>

          {/* Story cards: logo, backgrounds, phrase pools */}
          <div className="mt-6 mb-2 overline">Story cards</div>
          <Section
            title="Logo, generic backgrounds & phrase pools"
            defaultOpen={false}
          >
            <StoryCardsSection
              value={draft}
              onChange={(patch) => {
                setDraft(patch);
                setDirty(true);
              }}
            />
          </Section>

          {/* ─── CONNECTIONS ───────────────────────────────────────
              All third-party integrations grouped under a single
              subhead, each as its own expandable accordion. Order:
              Telegram → WordPress → Social Media → News Sources. */}
          <div className="mt-6 mb-2 overline">Connections</div>

          <Section title="Telegram integration" defaultOpen={false}>
            <TelegramSection />
          </Section>

          {/* Prospects inbox — each user's own sales mailbox on this
              property. Opens by itself when the mailbox sign-in returns
              here with ?mail_status=… */}
          <div id="sales-inbox">
            <Section
              title="Sales inbox · Prospects"
              defaultOpen={
                typeof window !== "undefined" &&
                (new URLSearchParams(window.location.search).has("mail_status") ||
                  window.location.hash === "#sales-inbox")
              }
            >
              <SalesInboxSection />
            </Section>
          </div>

          {/* WordPress connection — per-property creds */}
          <Section title="WordPress connection" defaultOpen={false}>
            <p className="text-xs text-[var(--ink-muted)] mb-3">
              Each property has its own WordPress site. Provide the site URL,
              an author-role WP user, and an Application Password (WP Admin →
              Users → Profile → Application Passwords).
            </p>
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-[var(--ink-muted)] font-mono-display">
                  Site URL
                </label>
                <input
                  data-testid="wp-url-input"
                  type="url"
                  value={draft.wp_url}
                  onChange={(e) => {
                    setDraft({ ...draft, wp_url: e.target.value });
                    setDirty(true);
                  }}
                  placeholder="https://www.example.com"
                  className="w-full mt-1 px-3 py-2 text-sm bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)]"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[var(--ink-muted)] font-mono-display">
                  Username
                </label>
                <input
                  data-testid="wp-username-input"
                  type="text"
                  value={draft.wp_username}
                  onChange={(e) => {
                    setDraft({ ...draft, wp_username: e.target.value });
                    setDirty(true);
                  }}
                  placeholder="wp-editor"
                  className="w-full mt-1 px-3 py-2 text-sm bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)]"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[var(--ink-muted)] font-mono-display">
                  Application Password
                </label>
                <input
                  data-testid="wp-password-input"
                  type="password"
                  value={draft.wp_app_password}
                  onChange={(e) => {
                    setDraft({ ...draft, wp_app_password: e.target.value });
                    setDirty(true);
                  }}
                  placeholder="Leave blank to keep the current password"
                  autoComplete="new-password"
                  className="w-full mt-1 px-3 py-2 text-sm bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)]"
                />
                <p className="text-[11px] text-[var(--ink-muted)] mt-1 font-mono-display">
                  Never displayed after save — leave empty on next edit to
                  keep it.
                </p>
              </div>
              <WpcomConnectionSection />
              <div>
                <label className="text-xs uppercase tracking-wider text-[var(--ink-muted)] font-mono-display">
                  Jetpack site ID (optional)
                </label>
                <input
                  data-testid="jetpack-site-id-input"
                  type="text"
                  value={draft.jetpack_site_id}
                  onChange={(e) => {
                    setDraft({ ...draft, jetpack_site_id: e.target.value });
                    setDirty(true);
                  }}
                  placeholder={wpHost(draft.wp_url) || "Derived from the site URL"}
                  className="w-full mt-1 px-3 py-2 text-sm bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)]"
                />
                <p className="text-[11px] text-[var(--ink-muted)] mt-1 font-mono-display">
                  Override only. Connecting above captures the blog ID
                  automatically — set this just if the captured value is
                  wrong for this site.
                </p>
              </div>
            </div>
          </Section>

          {/* Facebook Pages + Instagram Business accounts linked to this
              newspaper. Populated by the OAuth flow — see the Connect
              button inside the section. */}
          <Section
            title="Social media connections (Facebook & Instagram)"
            defaultOpen={false}
          >
            <SocialConnectionsSection />
          </Section>

          {/* Newsletter · MailerLite connection */}
          <Section title="Newsletter · MailerLite" defaultOpen={false}>
            <p className="text-xs text-[var(--ink-muted)] mb-3">
              Configure the MailerLite API token, sender identity, and
              the subscriber group this property&apos;s newsletter goes
              to. The token is stored server-side and never returned to
              the browser after save.
            </p>
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-[var(--ink-muted)] font-mono-display">
                  API token{" "}
                  {draft.mailerlite_api_token_set && (
                    <span className="ml-1 text-emerald-700 normal-case tracking-normal">
                      · currently connected
                    </span>
                  )}
                </label>
                <input
                  data-testid="mailerlite-token-input"
                  type="password"
                  value={draft.mailerlite_api_token}
                  onChange={(e) => {
                    setDraft({ ...draft, mailerlite_api_token: e.target.value });
                    setDirty(true);
                  }}
                  placeholder={
                    draft.mailerlite_api_token_set
                      ? "•••••••• (leave blank to keep)"
                      : "MailerLite → Integrations → Developer API"
                  }
                  className="w-full mt-1 px-3 py-2 text-sm bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)]"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider text-[var(--ink-muted)] font-mono-display">
                    From name
                  </label>
                  <input
                    data-testid="mailerlite-from-name"
                    value={draft.mailerlite_from_name}
                    onChange={(e) => {
                      setDraft({ ...draft, mailerlite_from_name: e.target.value });
                      setDirty(true);
                    }}
                    placeholder="Gringo Gazette"
                    className="w-full mt-1 px-3 py-2 text-sm bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)]"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-[var(--ink-muted)] font-mono-display">
                    From email (must be verified in MailerLite)
                  </label>
                  <input
                    data-testid="mailerlite-from-email"
                    value={draft.mailerlite_from_email}
                    onChange={(e) => {
                      setDraft({ ...draft, mailerlite_from_email: e.target.value });
                      setDirty(true);
                    }}
                    placeholder="editor@example.com"
                    className="w-full mt-1 px-3 py-2 text-sm bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)]"
                  />
                </div>
              </div>
              <MailerLiteAudience
                allSubscribers={draft.mailerlite_all_subscribers}
                groupIds={draft.mailerlite_group_ids}
                language={draft.mailerlite_language}
                legacyGroupId={draft.mailerlite_group_id}
                tokenSaved={draft.mailerlite_api_token_set}
                onChange={(patch) => {
                  setDraft({ ...draft, ...patch });
                  setDirty(true);
                }}
              />
              <div>
                <label className="text-xs uppercase tracking-wider text-[var(--ink-muted)] font-mono-display">
                  Footer physical address (CAN-SPAM)
                </label>
                <input
                  data-testid="newsletter-address"
                  value={draft.newsletter_footer_address}
                  onChange={(e) => {
                    setDraft({ ...draft, newsletter_footer_address: e.target.value });
                    setDirty(true);
                  }}
                  placeholder="123 Marina Blvd, Cabo San Lucas, BCS, Mexico&#10;(one line per address line, or commas)"
                  className="w-full mt-1 px-3 py-2 text-sm bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)]"
                />
              </div>
              <NewsletterThemeEditor
                theme={draft.newsletter_theme}
                defaults={settings?.newsletter_theme_defaults}
                fontOptions={settings?.newsletter_font_options}
                onChange={(next) => patchDraft({ newsletter_theme: next })}
              />
              <NewsletterPromptOverrides draft={draft} setDraft={setDraft} setDirty={setDirty} />
            </div>
          </Section>

          {/* News sources */}
          <SourcesSection
            sources={sources}
            onUpdate={updateSource}
            onRemove={removeSource}
            onAdd={addSource}
          />

          {/* Google News locales */}
          <GoogleNewsLocalesSection
            options={settings?.google_news_locale_options}
            selected={draft.google_news_locales}
            onChange={(next) => patchDraft({ google_news_locales: next })}
          />

          {/* House rules */}
          <div className="mt-6 mb-2 overline">Editorial style brief</div>
          {Object.entries(HOUSE_RULE_LABELS).map(([k, label]) => (
            <Section key={k} title={label}>
              <TextareaField
                testId={`${SETTINGS.houseRuleField}-${k}`}
                label={label}
                value={houseRules[k]}
                onChange={(v) => updateHouseRule(k, v)}
                rows={
                  k === "identity" ||
                  k === "story_structure" ||
                  k === "community_news_standard" ||
                  k === "quick_rules"
                    ? 8
                    : 5
                }
              />
            </Section>
          ))}

          {/* Writers */}
          <div className="mt-6 mb-2 overline">Writer voices</div>
          <AddWriterForm wpAuthors={wpAuthors} onAdd={addWriter} />
          {Object.entries(writers).map(([wk, w]) => (
            <Section
              key={wk}
              title={`${w.name || wk}`}
              right={
                <button
                  data-testid={`settings-writer-remove-${wk}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeWriter(wk);
                  }}
                  title="Remove this writer"
                  className="text-[var(--ink-muted)] hover:text-red-600 p-1"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              }
            >
              <div className="text-xs text-[var(--ink-muted)] font-mono-display mb-3">
                {wk}
              </div>

              {/* WP Author mapping — sits at top since it's the most operational */}
              <label className="block mb-4 bg-orange-50/40 border border-orange-200 p-3">
                <span className="overline block mb-1.5 flex items-center gap-1.5">
                  <User className="w-3 h-3" /> WordPress author account
                </span>
                <select
                  data-testid={`${SETTINGS.writerAuthorSelect}-${wk}`}
                  value={w.wordpress_author_id ?? ""}
                  onChange={(e) =>
                    updateWriterField(
                      wk,
                      "wordpress_author_id",
                      e.target.value ? parseInt(e.target.value, 10) : null
                    )
                  }
                  className="w-full px-3 py-2 border border-[var(--border)] bg-white text-sm outline-none focus:border-[var(--slate-900)]"
                >
                  <option value="">— No WordPress author —</option>
                  {wpAuthors.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name} (id {u.id})
                    </option>
                  ))}
                </select>
                <p className="text-[11px] text-[var(--ink-muted)] mt-1.5">
                  Articles written by {w.name || wk} will be attributed to this
                  WordPress user on publish. Sync WordPress to refresh the list.
                </p>
              </label>

              {WRITER_FIELDS.map(({ key, label, rows }) => (
                <TextareaField
                  key={key}
                  testId={`${SETTINGS.writerField}-${wk}-${key}`}
                  label={label}
                  value={w[key] || ""}
                  onChange={(v) => updateWriterField(wk, key, v)}
                  rows={rows}
                />
              ))}
              <TextareaField
                testId={`${SETTINGS.writerField}-${wk}-assignment_tags`}
                label="Assignment tags (comma-separated)"
                value={(w.assignment_tags || []).join(", ")}
                onChange={(v) =>
                  updateWriterField(
                    wk,
                    "assignment_tags",
                    v
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean)
                  )
                }
                rows={2}
              />
            </Section>
          ))}
        </div>
      </div>
    </div>
  );
}

// Backend-provided defaults live in `settings.google_news_locale_options`;
// this list mirrors it so the section can still render if the settings
// query hasn't loaded yet (avoids a "no options" flash on first paint).
const GOOGLE_NEWS_LOCALE_FALLBACK = [
  { key: "es_mx", label: "Spanish · Mexico" },
  { key: "en_mx", label: "English · Mexico" },
  { key: "en_us", label: "English · United States" },
  { key: "en_ca", label: "English · Canada" },
];

function GoogleNewsLocalesSection({ options, selected, onChange }) {
  const opts = options && options.length ? options : GOOGLE_NEWS_LOCALE_FALLBACK;
  const selectedSet = new Set(selected || []);
  const toggle = (key) => {
    const next = new Set(selectedSet);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    // Preserve the display order defined by `opts` so the stored list
    // is deterministic and easy to reason about server-side.
    onChange(opts.filter((o) => next.has(o.key)).map((o) => o.key));
  };
  const activeCount = opts.filter((o) => selectedSet.has(o.key)).length;
  return (
    <Section
      title={`Google News locales · ${activeCount} active`}
      defaultOpen={false}
    >
      <div className="text-xs text-[var(--ink-muted)] mb-4">
        Choose which Google News editions the research feature queries.
        Each locale returns different stories (a Mexico-Spanish result set
        looks nothing like a US-English one). Pick at least one — leaving
        the list empty falls back to the built-in default set.
      </div>
      <div className="space-y-2">
        {opts.map((o) => {
          const on = selectedSet.has(o.key);
          return (
            <label
              key={o.key}
              data-testid={`google-news-locale-${o.key}`}
              className={`flex items-center gap-3 px-3 py-2 border cursor-pointer text-sm ${
                on
                  ? "border-[var(--ink)] bg-white"
                  : "border-[var(--border)] bg-[var(--secondary)]/40 text-[var(--ink-muted)]"
              }`}
            >
              <input
                type="checkbox"
                checked={on}
                onChange={() => toggle(o.key)}
                className="w-4 h-4 accent-[var(--accent)]"
              />
              <div className="flex-1">
                <div className="font-serif-display">{o.label}</div>
                <div className="text-[10px] uppercase tracking-wider font-mono-display text-[var(--ink-muted)]">
                  {o.key}
                </div>
              </div>
            </label>
          );
        })}
      </div>
    </Section>
  );
}

function SourcesSection({ sources, onUpdate, onRemove, onAdd }) {
  const [newName, setNewName] = useState("");
  const [newType, setNewType] = useState("rss");
  const [newValue, setNewValue] = useState("");
  const [newLocale, setNewLocale] = useState("");
  const [probing, setProbing] = useState(false);
  const [probeResult, setProbeResult] = useState(null);

  const runProbe = async () => {
    if (!newValue.trim()) {
      toast.error("Enter a URL or filter first");
      return;
    }
    setProbing(true);
    setProbeResult(null);
    try {
      const r = await api.probeSource({
        type: newType,
        value: newValue.trim(),
        locale: newType === "site_filter" ? newLocale || null : null,
      });
      setProbeResult(r);
      if (r.ok) toast.success(`Feed returned ${r.entries} entries`);
      else toast.error("Feed returned no entries");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Probe failed");
      setProbeResult({ ok: false, entries: 0 });
    } finally {
      setProbing(false);
    }
  };

  const handleAdd = () => {
    const name = newName.trim();
    const value = newValue.trim();
    if (!name || !value) {
      toast.error("Name and URL/filter are required");
      return;
    }
    const payload = { name, type: newType, value };
    if (newType === "site_filter") payload.locale = newLocale || "";
    onAdd(payload);
    setNewName("");
    setNewValue("");
    setNewLocale("");
    setProbeResult(null);
    toast.success(`Added ${name} — remember to Save changes`);
  };

  const rssCount = sources.filter((s) => s.type === "rss" && s.enabled).length;
  const filterCount = sources.filter(
    (s) => s.type === "site_filter" && s.enabled
  ).length;
  const scrapeCount = sources.filter(
    (s) => s.type === "website_scrape" && s.enabled
  ).length;

  return (
    <Section
      title={`News sources · ${sources.filter((s) => s.enabled).length} active`}
      defaultOpen={false}
    >
      <div className="text-xs text-[var(--ink-muted)] mb-4">
        These sources are queried on every research run. RSS feeds are
        fetched directly. Site filters run through Google News (useful
        for outlets without a working RSS). Website scrapes use Firecrawl
        + LLM extraction to pull articles from landing pages that offer
        neither RSS nor Google News coverage. Toggle a source off to
        skip it temporarily; remove to delete permanently.
      </div>

      {/* Existing sources */}
      <div className="border border-[var(--border)] bg-[var(--bg)] mb-6">
        <div className="grid grid-cols-[1fr_auto_auto] sm:grid-cols-[auto_1fr_2fr_auto_auto] gap-2 px-3 py-2 border-b border-[var(--border)] text-[10px] uppercase tracking-wider text-[var(--ink-muted)] font-mono-display">
          <div className="hidden sm:block">Type</div>
          <div>Name</div>
          <div className="hidden sm:block">URL / Filter</div>
          <div className="text-center">On</div>
          <div></div>
        </div>
        {sources.length === 0 && (
          <div className="p-4 text-sm text-[var(--ink-muted)] italic">
            No sources configured. Add one below.
          </div>
        )}
        {sources.map((s, i) => (
          <div
            key={i}
            data-testid={`${SETTINGS.sourceRow}-${i}`}
            className={`grid grid-cols-[1fr_auto_auto] sm:grid-cols-[auto_1fr_2fr_auto_auto] gap-2 px-3 py-2 border-b border-[var(--border)] last:border-b-0 items-center text-sm ${
              s.enabled ? "bg-white" : "bg-[var(--secondary)] opacity-70"
            }`}
          >
            <div className="hidden sm:flex items-center gap-1 text-xs text-[var(--ink-muted)] font-mono-display">
              {s.type === "rss" ? (
                <Rss className="w-3 h-3" />
              ) : s.type === "website_scrape" ? (
                <Globe className="w-3 h-3" />
              ) : (
                <SearchIcon className="w-3 h-3" />
              )}
              <span>
                {s.type === "rss"
                  ? "RSS"
                  : s.type === "website_scrape"
                  ? "WEB"
                  : "SITE"}
              </span>
            </div>
            <input
              value={s.name}
              onChange={(e) => onUpdate(i, { name: e.target.value })}
              className="min-w-0 px-2 py-1 text-sm bg-transparent border-b border-transparent focus:border-[var(--ink)] outline-none font-serif-display"
            />
            <div className="min-w-0 hidden sm:flex items-center gap-2">
              <input
                value={s.value}
                onChange={(e) => onUpdate(i, { value: e.target.value })}
                className="flex-1 min-w-0 px-2 py-1 text-xs bg-transparent border-b border-transparent focus:border-[var(--ink)] outline-none font-mono-display truncate"
                spellCheck={false}
              />
              {s.type === "site_filter" && (
                <select
                  data-testid={`${SETTINGS.sourceRow}-${i}-locale`}
                  value={s.locale || ""}
                  onChange={(e) => onUpdate(i, { locale: e.target.value })}
                  title="Google News locale for this filter (empty = property default)"
                  className="text-[10px] font-mono-display uppercase tracking-wider px-1.5 py-0.5 border border-[var(--border)] bg-[var(--bg)] hover:bg-white cursor-pointer max-w-[85px]"
                >
                  <option value="">Default</option>
                  {GOOGLE_NEWS_LOCALE_FALLBACK.map((o) => (
                    <option key={o.key} value={o.key}>
                      {o.key.replace("_", "·").toUpperCase()}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <label className="inline-flex items-center gap-1 cursor-pointer">
              <input
                type="checkbox"
                data-testid={`${SETTINGS.sourceEnableToggle}-${i}`}
                checked={!!s.enabled}
                onChange={(e) => onUpdate(i, { enabled: e.target.checked })}
                className="w-4 h-4 accent-[var(--accent)]"
              />
            </label>
            <button
              data-testid={`${SETTINGS.sourceRemoveButton}-${i}`}
              onClick={() => onRemove(i)}
              aria-label="Remove source"
              className="p-1 text-[var(--ink-muted)] hover:text-red-600"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        <div className="px-3 py-2 border-t border-[var(--border)] flex items-center gap-4 text-[11px] font-mono-display text-[var(--ink-muted)]">
          <span>{rssCount} RSS · {filterCount} site filters · {scrapeCount} website scrapes</span>
          <span className="ml-auto">
            Save changes to persist edits
          </span>
        </div>
      </div>

      {/* Add new */}
      <div className="border border-[var(--border)] bg-white p-4">
        <div className="overline mb-3">Add a source</div>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_2fr_auto] gap-2">
          <input
            data-testid={SETTINGS.sourceNameInput}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Display name"
            className="px-3 py-2 text-sm border border-[var(--border)] outline-none focus:border-[var(--slate-900)] bg-white"
          />
          <select
            data-testid={SETTINGS.sourceTypeSelect}
            value={newType}
            onChange={(e) => {
              setNewType(e.target.value);
              setProbeResult(null);
            }}
            className="px-3 py-2 text-sm border border-[var(--border)] outline-none focus:border-[var(--slate-900)] bg-white"
          >
            <option value="rss">RSS feed URL</option>
            <option value="site_filter">Site filter (Google News)</option>
            <option value="website_scrape">Website (Firecrawl)</option>
          </select>
          <input
            data-testid={SETTINGS.sourceValueInput}
            value={newValue}
            onChange={(e) => {
              setNewValue(e.target.value);
              setProbeResult(null);
            }}
            placeholder={
              newType === "rss"
                ? "https://outlet.mx/feed/"
                : newType === "website_scrape"
                ? "https://outlet.gov/news"
                : "site:outlet.mx"
            }
            className="px-3 py-2 text-sm border border-[var(--border)] outline-none focus:border-[var(--slate-900)] bg-white font-mono-display"
            spellCheck={false}
          />
          {/* Site-filter-only locale override */}
          {newType === "site_filter" && (
            <select
              data-testid="source-new-locale"
              value={newLocale}
              onChange={(e) => {
                setNewLocale(e.target.value);
                setProbeResult(null);
              }}
              title="Google News locale for this filter (empty = property default)"
              className="px-3 py-2 text-sm border border-[var(--border)] outline-none focus:border-[var(--slate-900)] bg-white font-mono-display sm:col-span-2"
            >
              <option value="">Locale · use property default</option>
              {GOOGLE_NEWS_LOCALE_FALLBACK.map((o) => (
                <option key={o.key} value={o.key}>
                  Locale · {o.label} ({o.key})
                </option>
              ))}
            </select>
          )}
          <div className="flex gap-2">
            <button
              data-testid={SETTINGS.sourceProbeButton}
              onClick={runProbe}
              disabled={probing || !newValue.trim()}
              className="px-3 py-2 text-sm border border-[var(--border)] hover:bg-[var(--secondary)] disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              {probing ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Radio className="w-3.5 h-3.5" />
              )}
              Test
            </button>
            <button
              data-testid={SETTINGS.sourceAddButton}
              onClick={handleAdd}
              className="px-3 py-2 text-sm bg-[var(--slate-900)] text-white hover:bg-black inline-flex items-center gap-1.5"
            >
              <Plus className="w-3.5 h-3.5" />
              Add
            </button>
          </div>
        </div>

        {probeResult && (
          <div className="mt-3 border border-[var(--border)] bg-[var(--bg)] p-3 text-xs">
            <div className="flex items-center gap-2 mb-1">
              {probeResult.ok ? (
                <>
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                  <span className="text-emerald-800">
                    Working — {probeResult.entries} entries returned
                  </span>
                </>
              ) : (
                <>
                  <XCircle className="w-3.5 h-3.5 text-red-600" />
                  <span className="text-red-800">
                    No entries returned. Double-check the URL or try a different
                    type.
                  </span>
                </>
              )}
            </div>
            {probeResult.sample?.length > 0 && (
              <ul className="mt-1 space-y-0.5">
                {probeResult.sample.map((s, i) => (
                  <li key={i} className="text-[var(--ink-muted)] truncate">
                    · {s.title}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <p className="mt-3 text-[11px] text-[var(--ink-muted)]">
          <span className="font-semibold">Tip:</span> Most WordPress sites have
          an RSS feed at <code className="font-mono-display">/feed/</code>. If a
          site doesn&rsquo;t expose one, add it as a Site filter with the format{" "}
          <code className="font-mono-display">site:outlet.mx</code> — this makes
          Google News query only that domain.
        </p>
      </div>
    </Section>
  );
}

function AddWriterForm({ wpAuthors = [], onAdd }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [authorId, setAuthorId] = useState("");

  const reset = () => {
    setName("");
    setAuthorId("");
    setOpen(false);
  };

  if (!open) {
    return (
      <div className="mb-4">
        <button
          data-testid="settings-writer-add-toggle"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-dashed border-[var(--border)] hover:border-[var(--ink)] hover:bg-[var(--secondary)]"
        >
          <Plus className="w-3.5 h-3.5" />
          Add writer
        </button>
      </div>
    );
  }

  return (
    <div
      data-testid="settings-writer-add-form"
      className="mb-4 bg-white border border-[var(--border)] p-4 space-y-3"
    >
      <div className="flex items-center justify-between">
        <div className="overline">New writer</div>
        <button
          onClick={reset}
          className="text-[var(--ink-muted)] hover:text-[var(--ink)]"
          aria-label="Cancel"
        >
          <XCircle className="w-4 h-4" />
        </button>
      </div>

      <div>
        <label className="overline text-[var(--ink-muted)] block mb-1.5">
          Name <span className="text-red-600">*</span>
        </label>
        <input
          data-testid="settings-writer-add-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Sofia Alvarez"
          className="w-full border border-[var(--border)] px-3 py-2 text-sm focus:outline-none focus:border-[var(--slate-900)]"
        />
      </div>

      <div>
        <label className="overline text-[var(--ink-muted)] block mb-1.5">
          WordPress author <span className="normal-case tracking-normal text-[var(--ink-muted)]/80 ml-1">— optional, can be set later</span>
        </label>
        <select
          data-testid="settings-writer-add-author-id"
          value={authorId}
          onChange={(e) => setAuthorId(e.target.value)}
          className="w-full border border-[var(--border)] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[var(--slate-900)]"
        >
          <option value="">— No WordPress author —</option>
          {wpAuthors.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name} (id {u.id})
            </option>
          ))}
        </select>
      </div>

      <div className="text-[11px] text-[var(--ink-muted)]">
        A blank voice profile will be created — fill in voice, style traits and
        assignment tags in the writer section below, then click Save changes at
        the top of the page.
      </div>

      <div className="flex items-center gap-2 pt-1">
        <button
          data-testid="settings-writer-add-submit"
          onClick={() => {
            if (onAdd({ name, wordpressAuthorId: authorId })) reset();
          }}
          disabled={name.trim().length < 2}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[var(--slate-900)] text-white hover:bg-black disabled:opacity-50"
        >
          <Plus className="w-3.5 h-3.5" />
          Add writer
        </button>
        <button
          onClick={reset}
          className="px-3 py-1.5 text-sm text-[var(--ink-muted)] hover:text-[var(--ink)]"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}



/**
 * MailerLite subscriber-group picker.
 *
 * The list of groups is fetched from `/api/newsletter/groups`, which
 * proxies MailerLite. The token has to be persisted server-side first
 * (we never send the token from the browser again), so the dropdown
 * is only enabled when `mailerlite_api_token_set` is true. When it's
 * NOT set we fall back to a manual input so an editor testing the
 * flow (or one whose ML plan blocks the API) can still paste a
 * numeric ID and keep moving.
 *
 * Refresh button re-fetches after ML changes (new groups added, etc.)
 * without requiring the editor to reload the whole Settings page.
 */
// MailerLiteGroupPicker removed — replaced by the MailerLiteAudience
// component (All subscribers / multi-group / language), which lives in
// its own file and is imported at the top of this module.

/**
 * Newsletter prompt overrides — three long-form textareas that let
 * the editor customise the Subject / Preheader / Intro prompts used
 * when composing a newsletter. Leaving a field empty falls back to
 * the built-in default served by `/api/newsletter/prompt-defaults`,
 * which we fetch once so we can show it as the textarea placeholder.
 * "Reset to default" clears the override for that specific field.
 */
function NewsletterPromptOverrides({ draft, setDraft, setDirty }) {
  const { data: defaults } = useQuery({
    queryKey: ["newsletter-prompt-defaults"],
    queryFn: () => api.newsletterPromptDefaults(),
    staleTime: 5 * 60_000,
  });

  const rows = [
    ["newsletter_subject_prompt", "Subject line prompt", defaults?.subject_prompt || ""],
    ["newsletter_preheader_prompt", "Preheader prompt", defaults?.preheader_prompt || ""],
    ["newsletter_intro_prompt", "Intro prompt", defaults?.intro_prompt || ""],
  ];

  return (
    <div className="pt-2 border-t border-[var(--border)] mt-2 space-y-4">
      <div>
        <h3 className="font-serif-display text-base text-[var(--ink)]">
          AI compose prompts
        </h3>
        <p className="text-[11px] text-[var(--ink-muted)] mt-0.5">
          Leave a field empty to use the built-in default (shown as
          placeholder). Overrides apply the next time you click Preview
          on the Newsletter page.
        </p>
      </div>
      {rows.map(([k, label, ph]) => (
        <div key={k}>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs uppercase tracking-wider text-[var(--ink-muted)] font-mono-display">
              {label}
            </label>
            {draft[k] && (
              <button
                type="button"
                data-testid={`${k}-reset`}
                onClick={() => {
                  setDraft({ ...draft, [k]: "" });
                  setDirty(true);
                }}
                className="text-[10px] text-[var(--accent)] hover:underline font-mono-display uppercase tracking-wider"
              >
                Reset to default
              </button>
            )}
          </div>
          <textarea
            data-testid={k}
            value={draft[k] || ""}
            onChange={(e) => {
              setDraft({ ...draft, [k]: e.target.value });
              setDirty(true);
            }}
            rows={8}
            placeholder={ph || "Loading default…"}
            className="w-full px-3 py-2 text-xs font-mono-display bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)] leading-relaxed"
          />
        </div>
      ))}
    </div>
  );
}


